// Layout do Admin
import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  Package,
  Building2,
  Building,
  Plug,
  X,
  Menu,
} from 'lucide-react';
import { cn } from '../../utils/cn';

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/app/admin' },
    { icon: Users, label: 'Usuários', path: '/app/admin/usuarios' },
    { icon: DollarSign, label: 'Assinaturas', path: '/app/admin/assinaturas' },
    { icon: Building2, label: 'Empresas', path: '/app/admin/empresas' },
    { icon: Building, label: 'Contas', path: '/app/admin/contas' },
    { icon: Package, label: 'Pacotes', path: '/app/admin/pacotes' },
    { icon: Plug, label: 'Configurações', path: '/app/admin/configuracoes' },
  ];

  const isActive = (path: string) => {
    if (path === '/app/admin') {
      return location.pathname === '/app/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100/30 dark:bg-gray-900">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-gray-50/95 dark:bg-gray-800 border-r border-gray-200/50 dark:border-gray-700',
          'transform transition-transform duration-300 ease-in-out',
          'lg:translate-x-0 lg:fixed',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">FA1</span>
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">FA1 Admin</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu */}
          <nav className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <ul className="space-y-1">
              {menuItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                      'text-gray-700 dark:text-gray-300',
                      isActive(item.path)
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-medium'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Overlay Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden ml-0 lg:ml-64">
        {/* Header */}
        <header className="bg-gray-50/95 dark:bg-gray-800 border-b border-gray-200/40 dark:border-gray-700 shadow-sm backdrop-blur-sm">
          <div className="flex items-center justify-between h-16 px-4 md:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors lg:hidden"
            >
              <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="flex-1" />
            <Link
              to="/app"
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"
            >
              Voltar para Plataforma
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
